<!-- =========================================================================================
  File Name: DeviceCategorySidebar.vue
  Description: Category - Sidebar component
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->
<i18n src="@/locales/modules/DeviceCategoryTranslate.json"></i18n>

<template>
	<el-dialog class="custom-dialog device-category-dialog" :visible.sync="isSidebarActiveLocal">
		<div slot="title">
			<div>
				<h4 class="uppercase font-bold block text-xl w-full">{{ $t("DeviceCategories") }}</h4>
			</div>
		</div>
		<PerfectScrollbar class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<div slot="header" class="flex flex-grow justify-end">
					<vs-button v-if="mixinIsCanCreateCategory" type="filled" class="w-1/2 sm:w-1/3 font-bold" @click="addNewData">{{ $t("Actions.AddNew") }}</vs-button>
				</div>

				<liquor-tree class="mt-5 overflow-visible sm:overflow-auto" :key="treeDataKey" :options="{ parentSelect: true }" :data="treeCategories">
					<div class="tree-text tree-item w-full flex justify-between" slot-scope="{ node }">
						<div class="content tree-item__content flex items-center">
							<template v-if="!node.hasChildren()">
								<i class="mr-2 nucleo nucleo-shape-oval-3"></i>
								{{ node.text }}
							</template>

							<template v-else>
								<i :class="[node.expanded() ? 'mr-2 nucleo nucleo-shape-oval-3' : 'mr-2 nucleo nucleo-e-add']"></i>
								{{ node.text }}
							</template>
						</div>

						<div class="whitespace-no-wrap tree-item__actions">
							<i v-if="mixinIsCanViewKPI" class="nucleo nucleo-questionnaire mr-3" @click.stop="manageChecklist(node)"></i>
							<i v-if="node.data.can_update" class="nucleo nucleo-edit mr-3" @click.stop="editData(node)"></i>
							<i
								v-if="node.data.can_delete"
								:class="[{ disabled: node.data.isInUse || (node.children && node.children.length) }, 'nucleo nucleo-trash-can']"
								@click.stop="!node.data.isInUse && !(node.children && node.children.length) && showDeleteConfirm(node.id)"
							></i>
						</div>
					</div>
				</liquor-tree>
			</div>
		</PerfectScrollbar>

		<popup-create-or-update-category-item :isPopupActive="isPopupActive" @closePopup="toggleCategoryPopup" :data="popupData" />
	</el-dialog>
</template>

<script>
import _ from "lodash";
import { kpiPermissions, categoryPermissions, getUserPermissions } from "@/mixins/mixins.js";
import { mapGetters } from "vuex";

import LiquorTree from "liquor-tree";
import PopupCreateOrUpdateCategoryItem from "./PopupCreateOrUpdateCategoryItem";

export default {
	mixins: [getUserPermissions({ ...kpiPermissions, ...categoryPermissions })],
	props: {
		isSidebarActive: {
			type: Boolean,
			required: true
		}
	},
	data() {
		return {
			isPopupActive: false,

			popupData: {},
			treeDataKey: 0,

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			}
		};
	},
	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		...mapGetters({
			getTreeCategories: "deviceManagement/getTreeCategories"
		}),
		treeCategories() {
			this.forceRerender();
			return this.getTreeCategories || [];
		}
	},
	methods: {
		forceRerender() {
			this.treeDataKey += 1;
		},
		toggleCategoryPopup(val = false) {
			this.isPopupActive = val;
		},
		addNewData() {
			this.popupData = {};
			this.toggleCategoryPopup(true);
		},
		editData(category) {
			try {
				this.popupData = category.data || {};
				this.toggleCategoryPopup(true);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			}
		},
		showDeleteConfirm(id) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Actions.Confirm"),
				text: this.$t("Notify.DeleteConfirm"),
				accept: () => this.deleteData(id),
				acceptText: this.$t("Actions.Agree"),
				cancelText: this.$t("Actions.Cancel")
			});
		},
		async deleteData(id) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("deviceManagement/deleteCategory", id);
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("DeleteCategorySuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		async manageChecklist(category) {
			this.$emit("manageChecklist", _.get(category, "data.id"), _.get(category, "data.kpis") || [], _.get(category, "data.name"));
		}
	},
	components: {
		LiquorTree,
		PopupCreateOrUpdateCategoryItem
	}
};
</script>

<style lang="scss" scoped>
.device-category-dialog {
	.scroll-area--data-list {
		height: auto;
		background-color: #fff;
		max-height: 100%;
	}

	::v-deep .tree {
		.tree-arrow {
			display: none;
		}

		.tree-text {
			.tree-item__content {
				.nucleo {
					color: #bdbdbd;
					font-size: 3px;

					&::before {
						font-weight: bold;
					}
				}
			}

			.tree-item__actions {
				.icon.disabled {
					color: #bdbdbd;

					&:hover {
						cursor: not-allowed;
					}
				}
			}
		}
	}
}
</style>
